/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { CirclesResponse, SessionUser, type Vertical } from '@/types/session';
import { DEFAULT_VERTICALS } from '@/types/task';

/**
 * Who am I, and which boards can I see.
 *
 * Neither is cached across a sign-out because both come from the cookie the
 * server verifies on every request; there is no client-side notion of "logged
 * in" to go stale. `<RequireSession>` reacts to a 401 from any query rather than
 * asking a separate "am I signed in?" endpoint first.
 */

export const sessionKeys = {
  me: ['session', 'me'] as const,
  circles: ['session', 'circles'] as const,
};

export function useMe() {
  return useQuery({
    queryKey: sessionKeys.me,
    queryFn: async () => SessionUser.parse(await api.get('/auth/me')),
    /**
     * The session is stable for as long as the cookie is. Polling it every 20
     * seconds like board data would be a request per person per 20s for an
     * answer that changes only at sign-in and sign-out.
     *
     * **Except while it is failing, and that exception is the whole point.**
     * `staleTime: Infinity` with no interval meant that once the three attempts
     * at `/auth/me` were spent, this query never asked again — so a server that
     * was unreachable for one second at boot left the app needing a manual
     * reload, for ever. Every other query on the board recovers on its own
     * because they carry the 20-second interval; this one had opted out of the
     * mechanism that would have saved it.
     *
     * That is not a hypothetical: `pm2 restart` is a step in every deploy, nginx
     * answers 502 while Express comes back up, and anybody holding the board open
     * across a deploy is in exactly this state. Five seconds while broken, never
     * while working.
     */
    refetchInterval: (query) => (query.state.status === 'error' ? 5_000 : false),
    staleTime: Infinity,
  });
}

/**
 * The board on screen, named. Used by the heading and the switcher alike.
 *
 * There used to be a third answer here — `combinedLabel()`, which said "Both"
 * at exactly two circles and "All circles" at three or more, for the combined
 * board. **That board is gone**: a circle chooses its own columns now, and four
 * circles with four different column sets have no honest set of columns to draw.
 * The helper and its four unit tests went with it.
 *
 * `'Qollab'` is the fallback for *before* circles have loaded and for somebody
 * with none. It was never the name of a board and still is not.
 */
export function activeCircleLabel(circles: CirclesResponse | undefined): string {
  if (!circles) return 'Qollab';
  return circles.circles.find((c) => c.id === circles.activeId)?.name ?? 'Qollab';
}

/**
 * The columns of the board on screen.
 *
 * **One definition, read by every view.** Eleven components imported a
 * `VERTICALS` constant before this; they read this instead, so a renamed column
 * changes the board, the list's grouping, the calendar's legend, the filter
 * chips and both modals' chip rows in one go — and none of them can be the one
 * place that still says "Discovery".
 *
 * Falls back to the five defaults while the session is still loading and for a
 * circle the server sent no columns for, which is the same thing an empty
 * `circle_verticals` means server-side.
 */
export function verticalsOf(circles: CirclesResponse | undefined, circleId?: string): Vertical[] {
  const id = circleId ?? circles?.activeId;
  const found = circles?.circles.find((c) => c.id === id)?.verticals;
  return found && found.length ? found : DEFAULT_VERTICALS;
}

export function useVerticals(circleId?: string): Vertical[] {
  const { data } = useCircles();
  return verticalsOf(data, circleId);
}

export function useCircles() {
  return useQuery({
    queryKey: sessionKeys.circles,
    queryFn: async () => CirclesResponse.parse(await api.get('/api/circles')),
  });
}
